import { type FC, useCallback } from 'react';

import { Link } from 'dibs-elements/exports/Link';
import ArrowLeft from 'dibs-icons/exports/legacy/ArrowLeft';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import ArrowUp from 'dibs-icons/exports/legacy/ArrowUp';
import ArrowDown from 'dibs-icons/exports/legacy/ArrowDown';
import ChevronLeft from 'dibs-icons/exports/ChevronLeft';
import ChevronRight from 'dibs-icons/exports/ChevronRight';
import classnames from 'classnames';
import { type OnArrowClick } from '../types';

import { useInternalScrollCarousel } from '../helpers/InternalScrollCarouselContext';

import styles from './Arrow.scss';

//Arrow
const ARROW_ICONS = {
    left: ArrowLeft,
    right: ArrowRight,
    up: ArrowUp,
    down: ArrowDown,
} as const;

const NEXT_ARROW_DIRECTION = {
    horizontal: 'right',
    vertical: 'down',
} as const;

const PREV_ARROW_DIRECTION = {
    horizontal: 'left',
    vertical: 'up',
} as const;

export const Arrow: FC<{
    type: 'previous' | 'next';
    onArrowClick?: OnArrowClick;
    noInnerSpacing?: boolean;
}> = ({ type, onArrowClick, noInnerSpacing }) => {
    const {
        carouselOrientation,
        currentIndex,
        stepSize,
        totalItems,
        isInfinite,
        itemsToShow,
        hasMultipleSlides,
        setCurrentIndex,
        isFullWidth,
        arrowClassName,
    } = useInternalScrollCarousel();

    const isPrev = type === 'previous';

    const onClick = useCallback((): void => {
        const index = isPrev ? currentIndex - stepSize : currentIndex + stepSize;
        setCurrentIndex(index);

        onArrowClick?.({ direction: isPrev ? 'previous' : 'next' });
    }, [currentIndex, isPrev, stepSize, setCurrentIndex, onArrowClick]);

    const endIndex = totalItems - 1;
    const showNextArrow = currentIndex + Math.floor(itemsToShow) <= endIndex;
    const showPrevArrow = currentIndex + (totalItems % stepSize) - stepSize >= 0;

    if (
        //if showArrows enabled always show both arrows on infinite carousels if there are items to scroll to
        (isInfinite && hasMultipleSlides) ||
        /**
         * if showArrows enabled on not infinite carousels:
         * - hide both arrows if only one slide
         * - hide the previous arrow on the first slide
         * - hide the next arrow on the last slide
         */
        (!isInfinite && hasMultipleSlides && (isPrev ? showPrevArrow : showNextArrow))
    ) {
        const arrowDirection = isPrev
            ? PREV_ARROW_DIRECTION[carouselOrientation]
            : NEXT_ARROW_DIRECTION[carouselOrientation];

        let Icon;

        if (isFullWidth) {
            const ChevronIcon = isPrev ? ChevronLeft : ChevronRight;

            Icon = (
                <div className={styles.chevronWrapper}>
                    <ChevronIcon variation="24" />
                </div>
            );
        } else {
            const ArrowIcon = ARROW_ICONS[arrowDirection];

            Icon = <ArrowIcon className={styles.arrow} />;
        }

        return (
            <Link
                className={classnames(styles.arrowLink, arrowClassName, {
                    [styles.noInnerRightSpacing]: isPrev && noInnerSpacing,
                    [styles.noInnerLeftSpacing]: !isPrev && noInnerSpacing,
                    [styles.fullWidth]: isFullWidth,
                    [styles.fullWidthLeft]: isPrev && isFullWidth,
                    [styles.fullWidthRight]: !isPrev && isFullWidth,
                })}
                onClick={onClick}
            >
                {Icon}
            </Link>
        );
    }

    //need to show arrow filler when arrow is hidden on not infinite carousels to prevent content flickering
    if (!isFullWidth) {
        return <div className={styles.arrowFiller} />;
    }

    return null;
};
